import { Link } from 'react-router-dom';
import { ArrowRight, Users, Globe, Activity } from 'lucide-react';
import { motion } from 'framer-motion';
import Marquee from '@/components/shared/Marquee';
import StatsCounter from '@/components/shared/StatsCounter';
import SectionReveal, { StaggerContainer, StaggerChild } from '@/components/shared/SectionReveal';

const FutureLogo = ({ size = 22 }) => (
  <img src="/images/future-logo-symbol.png" alt="Future" width={size} height={size} className="object-contain" style={{ filter: 'brightness(1.1)' }} />
);

export default function CommunityMarketing() {
  return (
    <div className="min-h-screen bg-[#091a16]" data-testid="community-marketing">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 md:px-10 py-4" style={{ background: 'rgba(19,46,40,.8)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,252,238,.06)' }}>
        <Link to="/" className="flex items-center gap-2.5"><FutureLogo size={20} /><span className="text-cream font-instrument font-bold text-[15px] tracking-[0.12em]">FUTURE</span></Link>
        <div className="flex items-center gap-4">
          <Link to="/login" className="btn-golden px-5 py-2 text-xs" data-testid="sign-in-btn">SIGN IN</Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-28 pb-20 bg-forest overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'linear-gradient(rgba(255,252,238,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,252,238,.05) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.h1 className="font-anton text-cream text-5xl md:text-7xl tracking-tight uppercase leading-none" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>The Future Community</motion.h1>
          <motion.p className="mt-6 font-instrument text-lg" style={{ color: 'rgba(255,252,238,.4)' }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>Where investors, founders, and operators converge to build Africa's future.</motion.p>
          <motion.div className="mt-6 flex justify-center gap-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
            {[{ icon: Users, label: '300+ Members' }, { icon: Globe, label: '16 Countries' }, { icon: Activity, label: '24/7 Active' }].map((stat, i) => (
              <div key={i} className="flex items-center gap-2 text-xs font-instrument font-semibold" style={{ color: 'rgba(255,252,238,.5)' }}>
                <stat.icon size={14} className="text-golden" />{stat.label}
              </div>
            ))}
          </motion.div>
          <motion.div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
            <Link to="/investors/invest" className="btn-golden px-8 py-3.5 text-sm inline-block" data-testid="invest-lp-btn">INVEST AS LP</Link>
            <Link to="/login" className="px-8 py-3.5 text-sm font-instrument font-semibold tracking-wider uppercase border border-golden/30 text-golden hover:bg-golden hover:text-forest transition-all inline-block" data-testid="join-community-btn">JOIN COMMUNITY</Link>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#091a16]">
        <div className="max-w-6xl mx-auto px-6">
          <SectionReveal><h2 className="font-anton text-cream text-3xl md:text-4xl tracking-tight uppercase mb-12 text-center">Track Record</h2></SectionReveal>
          <SectionReveal delay={0.1}><p className="font-instrument text-base leading-relaxed text-center max-w-3xl mx-auto mb-12" style={{ color: 'rgba(255,252,238,.45)' }}>We invest in bold and visionary leaders who are fearless champions for change on the continent. Our team has helped build some of Africa's most impactful, category-defining businesses. In less than a decade of investing, we've tracked outsized impact beyond financial returns.</p></SectionReveal>
          <StaggerContainer className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {[
              { v: 10, p: '$', s: 'M+', l: 'Capital Deployed' }, { v: 106, l: 'Portfolio Companies' },
              { v: 1, p: '$', s: 'B+', l: 'Follow-on Capital' }, { v: 2, l: 'Unicorns' },
              { v: 6, l: 'Exits' }, { v: 4000, s: '+', l: 'Direct Jobs Created' },
              { v: 90, s: '%', l: 'African Founder' }, { v: 40, s: '%', l: 'Female Founder' },
              { v: 10, s: '+', l: 'Countries' },
            ].map((stat, i) => (
              <StaggerChild key={i}><StatsCounter value={stat.v} prefix={stat.p || ''} suffix={stat.s || ''} label={stat.l} /></StaggerChild>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Foundation Focus Areas */}
      <section className="py-20 bg-forest">
        <div className="max-w-5xl mx-auto px-6">
          <SectionReveal>
            <span className="text-[10px] font-instrument font-bold tracking-[0.2em] text-golden/50 uppercase">// Foundation for Africa's Future</span>
            <h2 className="font-anton text-cream text-3xl md:text-4xl tracking-tight uppercase mt-4 mb-4">Are you invested in Africa's Future?</h2>
            <p className="font-instrument text-base leading-relaxed max-w-3xl" style={{ color: 'rgba(255,252,238,.5)' }}>We drive transformative impact by building an innovation ecosystem that empowers Africa's youth to build solutions to Africa's most pressing challenges. Join us in building a future where purpose meets prosperity for all.</p>
          </SectionReveal>
          <StaggerContainer className="grid md:grid-cols-3 gap-8 mt-12">
            {[
              { title: 'Talent Acceleration', desc: 'Boosting productivity through cutting-edge training and strategic job placement.' },
              { title: 'Unifying Platforms', desc: 'Funding technology and infrastructure that bridge divides and foster collaboration.' },
              { title: 'Visionary Leadership', desc: 'Empowering leaders to tackle challenges and create prosperity across all sectors.' },
            ].map((area, i) => (
              <StaggerChild key={i}>
                <div className="hover-card bg-forest-card p-8 border border-[rgba(255,252,238,.06)]" data-testid={`focus-area-${i}`}>
                  <h3 className="font-anton text-golden text-lg uppercase tracking-tight">{area.title}</h3>
                  <p className="mt-3 font-instrument text-sm leading-relaxed" style={{ color: 'rgba(255,252,238,.45)' }}>{area.desc}</p>
                </div>
              </StaggerChild>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Community Types */}
      <section className="py-16 bg-[#091a16]">
        <div className="max-w-6xl mx-auto px-6">
          <SectionReveal><h2 className="font-anton text-cream text-3xl md:text-4xl tracking-tight uppercase mb-4 text-center">Who's in the Community</h2></SectionReveal>
          <SectionReveal delay={0.1}><p className="font-instrument text-sm text-center max-w-2xl mx-auto mb-12" style={{ color: 'rgba(255,252,238,.4)' }}>Our community consists of over 300 high-profile local founders, venture firms, international business leaders, government policy-makers, and angel investors who share our vision to solve Africa's biggest challenges.</p></SectionReveal>
          <StaggerContainer className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {['LPs', 'Founders', 'Operators', 'Partners', 'Advisors', 'Alumni'].map((type, i) => (
              <StaggerChild key={i}>
                <div className="hover-card bg-forest-card p-6 border border-[rgba(255,252,238,.06)] text-center">
                  <h3 className="font-anton text-cream text-lg uppercase">{type}</h3>
                </div>
              </StaggerChild>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Get Involved */}
      <section className="py-16 bg-forest">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <SectionReveal>
            <h2 className="font-anton text-cream text-3xl md:text-4xl tracking-tight uppercase mb-4">Get Involved</h2>
            <p className="font-instrument text-sm leading-relaxed" style={{ color: 'rgba(255,252,238,.45)' }}>We are particularly excited about collaborating with angel investors and diasporans whose networks, skills and capacity are incredibly valuable to our startups and entrepreneurial communities.</p>
            <p className="mt-4 font-instrument text-sm" style={{ color: 'rgba(255,252,238,.4)' }}>Want to get involved? Email us:</p>
            <a href="mailto:foundation@future.africa" className="mt-2 inline-block font-instrument text-golden font-semibold text-lg hover:text-golden-light transition-colors" data-testid="foundation-email">foundation@future.africa</a>
          </SectionReveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-golden">
        <div className="max-w-xl mx-auto px-6 text-center">
          <SectionReveal>
            <h2 className="font-anton text-forest text-3xl md:text-4xl tracking-tight uppercase">It takes a village to raise a unicorn</h2>
            <Link to="/login" className="mt-8 inline-flex items-center gap-2 btn-golden border-2 border-forest px-8 py-3.5 text-sm" data-testid="join-now-btn">JOIN NOW <ArrowRight size={16} /></Link>
          </SectionReveal>
        </div>
      </section>

      <Marquee />
      <footer className="bg-[#091a16] py-6 px-6 md:px-12 flex items-center justify-between font-instrument" style={{ color: 'rgba(255,252,238,.22)', fontSize: '11px' }}><span>FUTURE</span><span>Since 2011</span><span className="hidden sm:inline">Lagos · Bridgetown · Cape Town · Houston</span></footer>
    </div>
  );
}
